import { useEffect, useState, useMemo } from 'react';
import { Breadcrumbs, Link } from '@mui/material';
import { Stack, Typography, Divider, Box } from '@mui/material';
import RoundedButton from '../../../components/RoundedButton/RoundedButton.tsx';
import {
	USER_MANAGEMENT_DASHBOARD,
	ADD_NEW_USER_SUBTITLE,
	BACK,
	USER_DETAILS,
	CONTACT_INFORMATION,
	ACCOUNT_INFORMATION,
	SUBMIT,
	ROLE,
	ACCOUNT,
	EDIT_USER,
	ADD_NEW_USER
} from '../../../constants/messages.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import useFormHook from '../../../hooks/useFormHook';
import { useAddUserMutation, useSaveUserMutation } from '../../../api/admin.api';
import ConfirmUserModal from '../../../components/Modals/ConfirmUserModal/ConfirmUserModal';
import ConfirmationField from '../../../components/Form/ConfirmationField.tsx';
import { phoneTypeOptions } from '../referenceOptions.ts';
import DirtyFormModal from '../../../components/Modals/DirtyFormModal/DirtyFormModal.tsx';
import {ColorBaseCharcoal} from "@huntington/honeycomb-design";
import { showAlert } from '../../../redux/slices/alertSlice.ts';
import { useDispatch } from 'react-redux';
import { SimpleTable } from '../../../components/SimpleTable/SimpleTable.tsx';
import type { UserFormMutation } from '../../../api/models/admin.model.ts';
import DuplicateEmailAddressModal, {
	duplicateEmailAddressError,
} from '../../../components/Modals/DuplicateEmailAddressModal/DuplicateEmailAddressModal.tsx';

export interface ConfirmUserFormAccounts {
	accountId: string;
	accountLabel: string;
	roleId: string;
	roleLabel: string;
}

export interface ConfirmUserForm {
	firstName: string;
	lastName: string;
	phoneType?: string;
	phoneNumber?: string;
	emailAddress: string;
	externalUserAccounts: ConfirmUserFormAccounts[];
}

type ConfirmUserLocationState = {
	formState: ConfirmUserForm;
	mode: MODE;
	userId?: number;
};

export type MODE = 'add' | 'edit';

const ROLE_TYPE_FIELD = 'role';
const ACCOUNT_TYPE_FIELD = 'accountType';

const getError = (err: any) => {
	if (err && typeof err === 'object' && 'data' in err) {
		const errorData = (err as { data: any }).data;
		if (errorData && typeof errorData === 'object' && 'error' in errorData) {
			return (errorData as { error: string }).error;
		}
	}
}

function ConfirmUser() {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		showDirtyModal,
		setShowDirtyModal,
		blockerProceed,
		setIsDirty
	} = useFormHook();
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showDuplicateErrorModal, setShowDuplicateErrorModal] = useState(false);
	const [addUser] = useAddUserMutation();
	const [saveUser] = useSaveUserMutation();
	const dispatch = useDispatch();

	// Get formState from location.state (passed from AddUser/EditUser)
	const {
		formState,
		userId,
		mode
	} = location.state as ConfirmUserLocationState;

	useEffect(() => {
		if (location.state?.isDirty) {
			setIsDirty(true);
		}
	}, [location.state, setIsDirty]);

	useEffect(() => {
		// Only allow navigation from /user-management/add-user
		if (!formState) {
			navigate('/user-management');
		}
	}, [formState, navigate]);

	const externalRows = useMemo(
		() =>
			(formState.externalUserAccounts || []).map((account) => {
				return {
					accountType: account.accountLabel,
					role: account.roleLabel
				};
			}),
		[formState.externalUserAccounts]
	);

	const phoneTypeLabel =
		phoneTypeOptions.find((option) => option.value === formState?.phoneType)?.label ||
		formState?.phoneType;

	// Handlers for navigation and confirmation
	const handleBack = () => {
		if (mode === 'edit') {
			navigate('/user-management/edit-user', { state: { formState, userId } });
		} else if (mode === 'add') {
			navigate('/user-management/add-user', { state: { formState } });
		}
	};

	const handleConfirmSubmit = async () => {
		setIsSubmitting(true);
		setIsDirty(false);
		try {
			const payload: UserFormMutation = {
				...formState,
				userType: 'external',
				emailAddress: formState.emailAddress,
				externalUserAccounts: formState.externalUserAccounts.map((acc: ConfirmUserFormAccounts) => ({
					roleId: acc.roleId,
					accountId: acc.accountId
				}))
			};
			if (mode === 'edit') {
				await saveUser({ userData: { ...payload, userId } }).unwrap();
				dispatch(showAlert({ message: 'User saved successfully.', type: 'success' }));
				navigate('/user-management', { state: { fetchUserDataFromCache: false } });
			} else if (mode === 'add') {
				await addUser({ userData: payload }).unwrap();
				setShowSuccessModal(true);
			} else {
				console.error('Invalid mode passed in while confirming user form:', mode);
				throw new Error('Invalid mode');
			}
		} catch (err) {
			const error = getError(err);
			if (error === duplicateEmailAddressError) {
				setShowDuplicateErrorModal(true);
				return;
			}
			dispatch(showAlert({ message: 'An unexpected error occurred. Please try again later.', type: 'error' }));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleNavigationConfirm = () => {
		blockerProceed();
	};

	const handleSuccessNavigate = () => {
		setShowSuccessModal(false);
		navigate('/user-management');
	};

	const handleAddAnotherUser = () => {
		setShowSuccessModal(false);
		navigate('/user-management/add-user');
	};

	const handleCloseDuplicateModal = () => {
		setShowDuplicateErrorModal(false);
	}

	return (
		<Stack spacing={2}>
			<Breadcrumbs aria-label="breadcrumb">
				<Link
					underline="always"
					color="inherit"
					onClick={() => navigate('/user-management')}
					sx={{cursor: 'pointer'}}
				>
					{USER_MANAGEMENT_DASHBOARD}
				</Link>
				<Typography sx={{color: 'text.primary', fontWeight: 'bold'}}>{mode === 'edit' ? EDIT_USER : ADD_NEW_USER}</Typography>
			</Breadcrumbs>
			<Typography
				variant="h1"
				component="h1"
				sx={{
					color: (theme) => theme.palette.brand.primary,
					fontWeight: 'bold',
					pl: '10px',
				}}
			>
				{USER_MANAGEMENT_DASHBOARD}
			</Typography>
			<Typography
				variant="subtitle1"
				component="h2"
				sx={{
					color: ColorBaseCharcoal,
					fontWeight: 'bold',
					pl: '10px',
				}}
			>
				{ADD_NEW_USER_SUBTITLE}
			</Typography>
			<Divider />
			<Typography
				variant="h5"
				component="h5"
				sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}
			>
				{USER_DETAILS}
			</Typography>
            
			<ConfirmationField header="First Name:" value={formState?.firstName} />
			<ConfirmationField header="Last Name:" value={formState?.lastName} />
			<Typography
				variant="h5"
				component="h5"
				sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}
			>
				{CONTACT_INFORMATION}
			</Typography>
			<ConfirmationField header="Phone Type (Optional):" value={phoneTypeLabel} />
			<ConfirmationField header="Phone Number (Optional):" value={formState?.phoneNumber} />
			<ConfirmationField header="Email:" value={formState?.emailAddress} />

			<Typography
				variant="h5"
				component="h5"
				sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}
			>
				{ACCOUNT_INFORMATION}
			</Typography>
			<Box sx={{ width: '900px' }}>
				<SimpleTable
					title="Account Specifications"
					columns={[
						{
							key: ACCOUNT_TYPE_FIELD,
							label: ACCOUNT,
						},
						{
							key: ROLE_TYPE_FIELD,
							label: ROLE,
						}
					]}
					data={externalRows}
				/>
			</Box>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
				<RoundedButton variantStyle="light" variant="outlined" onClick={handleBack} disabled={isSubmitting}>
					{BACK}
				</RoundedButton>
				<RoundedButton variantStyle="dark" variant="contained" onClick={handleConfirmSubmit} disabled={isSubmitting}>
					{SUBMIT}
				</RoundedButton>
			</Box>

			<ConfirmUserModal
				open={showSuccessModal}
				onAddAnotherUser={handleAddAnotherUser}
				onNavigate={handleSuccessNavigate}
			/>

			<DirtyFormModal
				open={showDirtyModal}
				onClose={() => setShowDirtyModal(false)}
				onNavigate={handleNavigationConfirm}
			/>
			<DuplicateEmailAddressModal open={showDuplicateErrorModal} onClose={handleCloseDuplicateModal} />
		</Stack>
	);
}

export default ConfirmUser;
